import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, RefreshCw, FileText, Zap, DollarSign, ChevronDown, Pencil, Copy
} from "lucide-react";
import { toast } from "sonner";
import { getSystemPromptAgent, hasSystemPromptMapping } from "../config/agentMapping";
import { formatInTimezone } from "../utils/timezone";

const PromptLogDetail = () => {
  const navigate = useNavigate();
  const { logId, sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryDropdownOpen, setSummaryDropdownOpen] = useState(false);
  
  // Get user's timezone preference
  const userTimezone = user?.timezone || "UTC";
  
  // Get index and highlight term from URL for scroll anchoring on back
  const promptIndex = searchParams.get("index") || "0";
  const highlightTerm = searchParams.get("highlight") || "";

  // Highlight matching text in content
  const highlightText = (text, term) => {
    if (!term || !text) return text;
    
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === term.toLowerCase()) {
        return (
          <mark 
            key={index} 
            className="bg-divine-gold/40 text-white px-0.5 rounded"
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  // Check admin access
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchPromptDetail();
  }, [logId, sessionId]);

  const fetchPromptDetail = async () => {
    try {
      setLoading(true);
      // Fetch all prompts for session and find the one with matching log_id
      const response = await authAxios.get(`/admin/plog/session/${sessionId}?limit=200`);
      const foundPrompt = response.data.prompts?.find(p => p.log_id === logId);
      
      if (foundPrompt) {
        setPrompt(foundPrompt);
      } else {
        toast.error("Prompt not found");
        navigate(`/plog`);
      }
    } catch (error) {
      console.error("Error fetching prompt:", error);
      toast.error("Failed to load prompt");
      navigate(`/plog`);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Navigate back to /plog with session and scroll anchor
    navigate(`/plog?session=${sessionId}&scrollTo=${promptIndex}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatCost = (cost) => {
    if (!cost || cost < 0.0001) return "<$0.0001";
    return `$${cost.toFixed(4)}`;
  };

  const getPromptTypeColor = (type) => {
    switch (type) {
      case "system": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "user": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "assistant": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default: return "bg-white/10 text-white/60 border-white/20";
    }
  };

  const getPromptTypeLabel = (type) => {
    switch (type) {
      case "system": return "System";
      case "user": return "User";
      case "assistant": return "Assistant";
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-divine-gold animate-spin" />
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <p className="text-white/40">Prompt not found</p>
      </div>
    );
  }

  // Get mapped agent name for edit link
  const mappedAgent = getSystemPromptAgent(prompt.agent_name);
  const canEdit = prompt.prompt_type === "system" && hasSystemPromptMapping(prompt.agent_name);

  // Build the display title line: (type)(agent • model) : title
  const displayTitle = prompt.display?.prompt_title || prompt.agent_name;
  const modelName = prompt.model || "unknown";

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header - Same style as /plog list */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-white/60 hover:text-white"
              data-testid="back-to-plog"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            {/* Header Title - Same as /plog */}
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-divine-gold" />
              <h1 className="font-cinzel text-xl text-white">PROMPT LOG</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Edit Prompt Button - Left of TOKEN display */}
            {canEdit && (
              <Link
                to={`/admin?tab=prompts&agent=${encodeURIComponent(mappedAgent)}`}
                target="_blank"
                className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300 transition-colors border border-purple-500/30"
                data-testid="edit-prompt-button"
              >
                <Pencil className="w-4 h-4" />
                Edit Prompt
              </Link>
            )}
            
            {/* Summary Dropdown in Header */}
            <div className="relative">
              <button
                onClick={() => setSummaryDropdownOpen(!summaryDropdownOpen)}
                className="flex items-center gap-4 px-4 py-2 bg-gradient-to-r from-divine-gold/10 to-purple-500/10 border border-divine-gold/30 rounded-lg hover:from-divine-gold/20 hover:to-purple-500/20 transition-all"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-divine-gold" />
                  <span className="text-white font-medium">{(prompt.total_tokens || 0).toLocaleString()}</span>
                  <span className="text-white/50 text-xs">tokens</span>
                </div>
                <div className="w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-divine-gold" />
                  <span className="text-divine-gold font-medium">{formatCost(prompt.estimated_cost)}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${summaryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Content */}
              {summaryDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setSummaryDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-black/95 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="p-4 space-y-4">
                      {/* Token Breakdown */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                          <div className="text-white/50 text-xs uppercase tracking-wider">Total</div>
                          <div className="text-xl font-bold text-white mt-1">
                            {(prompt.total_tokens || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                          <div className="text-blue-400/70 text-xs uppercase tracking-wider">Input</div>
                          <div className="text-lg font-semibold text-blue-400 mt-1">
                            {(prompt.input_tokens || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                          <div className="text-green-400/70 text-xs uppercase tracking-wider">Output</div>
                          <div className="text-lg font-semibold text-green-400 mt-1">
                            {(prompt.output_tokens || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      <div className="pt-3 border-t border-white/10 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-sm">Type</span>
                          <span className="text-white capitalize">{prompt.prompt_type}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-sm">Agent</span>
                          <span className="text-white">{prompt.agent_name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-sm">Model</span>
                          <span className="text-white">{prompt.model || "Unknown"}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-sm">Timestamp</span>
                          <span className="text-white/70 text-sm">
                            {prompt.created_at ? formatInTimezone(prompt.created_at, userTimezone) : "Unknown"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content - Full Prompt */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Highlight indicator */}
        {highlightTerm && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-divine-gold/10 border border-divine-gold/30 rounded-lg">
            <span className="text-divine-gold text-sm">Highlighting:</span>
            <span className="font-medium text-white uppercase">{highlightTerm}</span>
          </div>
        )}
        
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          {/* Prompt Title Area - Enhanced with moved header info */}
          <div className="px-6 py-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-white capitalize mb-1">
                  {getPromptTypeLabel(prompt.prompt_type)} Prompt
                </h2>
                {/* Moved header text here: (type)(agent • model) : title */}
                <p className={`text-sm ${getPromptTypeColor(prompt.prompt_type).split(' ')[1]}`}>
                  ({prompt.prompt_type})({prompt.agent_name} • {modelName}) : {displayTitle}
                </p>
                <p className="text-white/40 text-xs mt-1 font-mono">
                  log:{prompt.log_id}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(prompt.content)}
                className="text-white/60 hover:text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
          <pre className="p-6 text-sm text-white/80 whitespace-pre-wrap font-mono overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
            {highlightTerm ? highlightText(prompt.content, highlightTerm) : prompt.content}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default PromptLogDetail;
